import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Zap, Eye, EyeOff, Loader2, AlertCircle, Mail, X, KeyRound, ArrowLeft } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { getDashboardPath, requestPasswordReset } from '../services/auth.service'

// Google icon SVG
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
      <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  )
}

// ── Forgot Password modal ──────────────────────────────────────
function ForgotPasswordModal({ onClose }: { onClose: () => void }) {
  const [email, setEmail]       = useState('')
  const [loading, setLoading]   = useState(false)
  const [error, setError]       = useState<string | null>(null)
  const [result, setResult]     = useState<{ email: string } | null>(null)

  const inputBase: React.CSSProperties = {
    width: '100%', background: '#1a1a1a',
    border: '1px solid rgba(255,198,0,0.2)',
    borderRadius: 8, padding: '12px 16px', color: '#ffffff', fontSize: 14,
    fontFamily: 'DM Sans, sans-serif', outline: 'none',
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) { setError('Please enter your email address.'); return }
    setLoading(true); setError(null)
    const res = await requestPasswordReset(email.trim())
    setLoading(false)
    if (!res.success) { setError(res.error ?? 'Something went wrong.'); return }
    setResult({ email: email.trim() })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-sm rounded-2xl p-6 relative" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.35)', boxShadow: '0 0 48px rgba(0,0,0,0.9)' }}>

        {/* Close */}
        <button onClick={onClose} className="absolute top-4 right-4" style={{ color: '#555' }}><X size={18} /></button>

        {!result ? (
          /* ── Step 1: Enter email ── */
          <>
            <div className="flex flex-col items-center mb-6">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-3"
                style={{ background: 'rgba(255,198,0,0.1)', border: '1px solid rgba(255,198,0,0.2)' }}>
                <KeyRound size={20} style={{ color: '#ffc600' }} />
              </div>
              <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>Reset Password</h2>
              <p className="text-xs text-center mt-1" style={{ color: '#555' }}>
                Enter your email and we'll generate a new temporary password.
              </p>
            </div>

            {error && (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg mb-4 text-xs"
                style={{ background: 'rgba(204,51,0,0.1)', border: '1px solid rgba(204,51,0,0.3)', color: '#cc3300' }}>
                <AlertCircle size={13} className="flex-shrink-0" />{error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold mb-2" style={{ color: '#a0a0a0' }}>YOUR EMAIL</label>
                <input type="email" value={email}
                  onChange={e => { setEmail(e.target.value); setError(null) }}
                  placeholder="you@nexuserp.com" style={inputBase} autoFocus
                  onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
                  onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')}
                />
              </div>
              <button type="submit" disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-semibold text-sm disabled:opacity-60"
                style={{ background: '#ffc600', color: '#000', fontFamily: 'DM Sans, sans-serif', fontWeight: 600 }}>
                {loading ? <><Loader2 size={15} className="animate-spin" />Resetting…</> : 'Reset My Password'}
              </button>
            </form>

            <button onClick={onClose}
              className="w-full flex items-center justify-center gap-1.5 mt-3 text-xs py-2"
              style={{ color: '#555', fontFamily: 'DM Sans, sans-serif' }}>
              <ArrowLeft size={12} /> Back to login
            </button>
          </>
        ) : (
          /* ── Step 2: Check your email ── */
          <>
            <div className="flex flex-col items-center mb-5">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-3"
                style={{ background: 'rgba(232,160,0,0.12)', border: '1px solid rgba(255,198,0,0.25)' }}>
                <Mail size={20} style={{ color: '#e8a000' }} />
              </div>
              <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>Check Your Email</h2>
              <p className="text-xs text-center mt-1" style={{ color: '#555' }}>
                A password reset link has been sent.
              </p>
            </div>

            <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg mb-5 text-xs"
              style={{ background: 'rgba(255,198,0,0.05)', border: '1px solid rgba(255,198,0,0.18)', color: '#a0a0a0' }}>
              <Mail size={12} className="flex-shrink-0 mt-0.5" style={{ color: '#ffc600' }} />
              <span>
                We sent a reset link to <strong style={{ color: '#fff' }}>{result.email}</strong>.
                Click it to set a new password.
              </span>
            </div>

            <button onClick={onClose} className="w-full py-3 rounded-lg font-semibold text-sm"
              style={{ background: '#ffc600', color: '#000', fontFamily: 'DM Sans, sans-serif' }}>
              Back to Login
            </button>
          </>
        )}
      </div>
    </div>
  )
}

// ── Login page ────────────────────────────────────────────────
export default function Login() {
  const { login, loginGoogle, isAuthenticated, currentUser } = useAuth()
  const navigate = useNavigate()

  const [email, setEmail]           = useState('')
  const [password, setPassword]     = useState('')
  const [showPw, setShowPw]         = useState(false)
  const [loading, setLoading]       = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [error, setError]           = useState<string | null>(null)
  const [fieldError, setFieldError] = useState(false)
  const [showForgot, setShowForgot] = useState(false)

  useEffect(() => {
    if (isAuthenticated && currentUser) {
      navigate(getDashboardPath(currentUser.role), { replace: true })
    }
  }, [isAuthenticated, currentUser, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim() || !password.trim()) {
      setError('Please enter your email and password.')
      setFieldError(true)
      return
    }
    setLoading(true); setError(null); setFieldError(false)
    const result = await login({ email: email.trim(), password })
    setLoading(false)
    if (!result.success) {
      setError(result.error); setFieldError(true); return
    }
    navigate(getDashboardPath(result.user.role), { replace: true })
  }

  const inputBase: React.CSSProperties = {
    width: '100%', background: '#1a1a1a',
    border: `1px solid ${fieldError ? 'rgba(204,51,0,0.6)' : 'rgba(255,198,0,0.2)'}`,
    borderRadius: 8, padding: '12px 16px', color: '#ffffff', fontSize: 14,
    fontFamily: 'DM Sans, sans-serif', outline: 'none', transition: 'border-color 0.15s',
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#000000' }}>
      {/* Ambient glow */}
      <div className="absolute pointer-events-none"
        style={{ width: 480, height: 480, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,198,0,0.06) 0%, transparent 70%)',
          top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />

      <div className="relative w-full max-w-sm"
        style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.35)',
          borderRadius: 16, padding: '40px 36px', boxShadow: '0 0 48px rgba(0,0,0,0.8)' }}>

        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
            style={{ background: '#ffc600', boxShadow: '0 0 16px rgba(255,198,0,0.35)' }}>
            <Zap size={22} color="#000" fill="#000" />
          </div>
          <h1 className="text-2xl font-bold tracking-wide" style={{ fontFamily: 'Syne, sans-serif', color: '#ffc600' }}>
            Clickbait ERP
          </h1>
          <p className="text-sm mt-1" style={{ color: '#555555' }}>
            Internal workspace — authorised access only
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 px-4 py-3 rounded-lg mb-5 text-sm"
            style={{ background: 'rgba(204,51,0,0.1)', border: '1px solid rgba(204,51,0,0.35)', color: '#cc3300' }}>
            <AlertCircle size={15} className="flex-shrink-0" />{error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div>
            <label className="block text-xs font-semibold mb-2" style={{ color: '#a0a0a0', fontFamily: 'DM Sans, sans-serif' }}>
              EMAIL
            </label>
            <input type="email" autoComplete="email" autoFocus value={email}
              onChange={e => { setEmail(e.target.value); setError(null); setFieldError(false) }}
              placeholder="you@nexuserp.com" style={inputBase}
              onFocus={e => (e.currentTarget.style.borderColor = 'rgba(255,198,0,0.5)')}
              onBlur={e => (e.currentTarget.style.borderColor = fieldError ? 'rgba(204,51,0,0.6)' : 'rgba(255,198,0,0.2)')}
            />
          </div>

          {/* Password */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold" style={{ color: '#a0a0a0', fontFamily: 'DM Sans, sans-serif' }}>
                PASSWORD
              </label>
              <button type="button" onClick={() => setShowForgot(true)}
                className="text-xs transition-colors"
                style={{ color: '#555', fontFamily: 'DM Sans, sans-serif', background: 'none', border: 'none', cursor: 'pointer' }}
                onMouseEnter={e => (e.currentTarget.style.color = '#ffc600')}
                onMouseLeave={e => (e.currentTarget.style.color = '#555')}>
                Forgot password?
              </button>
            </div>
            <div className="relative">
              <input type={showPw ? 'text' : 'password'} autoComplete="current-password" value={password}
                onChange={e => { setPassword(e.target.value); setError(null); setFieldError(false) }}
                placeholder="••••••••" style={{ ...inputBase, paddingRight: 44 }}
                onFocus={e => (e.currentTarget.style.borderColor = 'rgba(255,198,0,0.5)')}
                onBlur={e => (e.currentTarget.style.borderColor = fieldError ? 'rgba(204,51,0,0.6)' : 'rgba(255,198,0,0.2)')}
              />
              <button type="button" onClick={() => setShowPw(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: '#555555', background: 'none', border: 'none', cursor: 'pointer' }}>
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {/* Submit */}
          <button type="submit" disabled={loading || googleLoading}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-semibold text-sm transition-all mt-2 disabled:opacity-60"
            style={{ background: '#ffc600', color: '#000000', fontFamily: 'DM Sans, sans-serif', fontWeight: 600,
              boxShadow: loading ? 'none' : '0 0 16px rgba(255,198,0,0.25)', cursor: loading ? 'not-allowed' : 'pointer' }}>
            {loading ? <><Loader2 size={16} className="animate-spin" />Verifying…</> : 'Enter Clickbait'}
          </button>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.07)' }} />
          <span className="text-xs" style={{ color: '#444' }}>or</span>
          <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.07)' }} />
        </div>

        {/* Google Sign In */}
        <button
          onClick={async () => { setGoogleLoading(true); await loginGoogle?.(); }}
          disabled={loading || googleLoading}
          className="w-full flex items-center justify-center gap-3 py-3 rounded-lg font-semibold text-sm transition-all disabled:opacity-60"
          style={{ background: '#1a1a1a', color: '#ffffff', fontFamily: 'DM Sans, sans-serif',
            border: '1px solid rgba(255,255,255,0.1)', cursor: googleLoading ? 'not-allowed' : 'pointer' }}>
          {googleLoading ? <Loader2 size={16} className="animate-spin" /> : <GoogleIcon />}
          {googleLoading ? 'Redirecting to Google…' : 'Continue with Google'}
        </button>

        <p className="text-center text-xs mt-6" style={{ color: '#333333', fontFamily: 'DM Sans, sans-serif' }}>
          Accounts are created by the system administrator
        </p>
      </div>

      {/* Forgot password modal */}
      {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} />}
    </div>
  )
}
